import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getSiteConfig, submitContact, formatApiErrorDetail } from "@/lib/api";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    getSiteConfig("contact").then(r => setCfg(r.data?.data || {})).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await submitContact(form);
      setSuccess(true);
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1670B9] mb-4">{cfg?.overline || "Contact"}</p>
          <h2 className="font-['Quicksand'] text-3xl lg:text-4xl font-medium tracking-tight text-slate-800" data-testid="contact-title">{cfg?.title || "Contactez-nous"}</h2>
        </motion.div>
        <div className="grid lg:grid-cols-5 gap-12">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-2 space-y-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#1670B9]/10 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-[#1670B9]" /></div>
              <div>
                <h3 className="font-['Quicksand'] font-semibold text-slate-800 mb-1">Téléphone</h3>
                <p className="text-sm text-slate-500">{cfg?.phone1 || "+229 01 66 000 000"}</p>
                {cfg?.phone2 && <p className="text-sm text-slate-500">{cfg.phone2}</p>}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#74CDD9]/10 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-[#74CDD9]" /></div>
              <div>
                <h3 className="font-['Quicksand'] font-semibold text-slate-800 mb-1">Email</h3>
                <p className="text-sm text-slate-500">{cfg?.email || "contact@uriebioz.com"}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#F49C03]/10 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-[#F49C03]" /></div>
              <div>
                <h3 className="font-['Quicksand'] font-semibold text-slate-800 mb-1">Adresse</h3>
                <p className="text-sm text-slate-500">
                  {cfg?.address_line1 || "LARBA – École Polytechnique d'Abomey-Calavi (EPAC)"}<br />
                  {cfg?.address_line2 || "Université d'Abomey-Calavi"}<br />
                  {cfg?.address_line3 || "Bénin, Afrique de l'Ouest"}
                </p>
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.3 }} className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 border border-slate-100 shadow-sm">
              {success && <div className="flex items-center gap-2 mb-6 p-4 rounded-xl bg-green-50 text-green-700 text-sm" data-testid="contact-success"><CheckCircle className="w-5 h-5" /> Message envoyé !</div>}
              {error && <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-600 text-sm" data-testid="contact-error">{error}</div>}
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nom</label>
                  <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1670B9]/20 focus:border-[#1670B9] transition-all bg-[#FAFAFA]" placeholder="Votre nom" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1670B9]/20 focus:border-[#1670B9] transition-all bg-[#FAFAFA]" placeholder="votre@email.com" data-testid="contact-email-input" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1670B9]/20 focus:border-[#1670B9] transition-all bg-[#FAFAFA] resize-none" placeholder="Votre message..." data-testid="contact-message-input" />
              </div>
              <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#F49C03] text-white font-semibold text-sm hover:bg-[#e08e00] transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md disabled:opacity-50" data-testid="contact-submit-button">
                <Send className="w-4 h-4" />{loading ? "Envoi..." : "Envoyer le message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
